import { ORDER_STATUSES } from "constants/orderStatus";
import { formatMoney } from "utils/money";

const ORDER_STATUS_ARABIC = Object.fromEntries(ORDER_STATUSES.map((entry) => [entry.en, entry.ar]));

const parseCoordinates = (rawLocation) => {
    if (typeof rawLocation !== "string") {
        return null;
    }

    const match = rawLocation.match(/(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/);

    if (!match) {
        return null;
    }

    const latitude = Number(match[1]);
    const longitude = Number(match[2]);

    if (
        !Number.isFinite(latitude) ||
        !Number.isFinite(longitude) ||
        latitude < -90 ||
        latitude > 90 ||
        longitude < -180 ||
        longitude > 180
    ) {
        return null;
    }

    return `${latitude},${longitude}`;
};

const buildOrderProductsText = (order) => {
    const products = Array.isArray(order?.cart_products) ? order.cart_products : [];

    if (products.length === 0) {
        return "لا توجد منتجات";
    }

    return products
        .map((productEntry, index) => {
            const quantity = productEntry?.quantity ?? 0;
            const title = productEntry?.product?.title || "منتج";
            const options = Array.isArray(productEntry?.options) ? productEntry.options : [];
            const optionsText =
                options.length > 0
                    ? options
                          .map((option) => option?.name)
                          .filter(Boolean)
                          .join("، ")
                    : "لا يوجد";

            return `${index + 1}. ${title}
   الكمية: ${quantity}
   الإضافات: ${optionsText}`;
        })
        .join("\n");
};

const resolveVendorName = (order) => {
    return (
        order?.cart_products?.[0]?.product?.user?.name || order?.vendor?.user?.name || "غير محدد"
    );
};

const resolveLocalizedOrderStatus = (status) => {
    if (!status) {
        return "غير محدد";
    }

    return ORDER_STATUS_ARABIC[status] || status;
};

const resolveGoogleMapsLocation = (rawLocation) => {
    if (!rawLocation) {
        return "غير محدد";
    }

    const location = `${rawLocation}`.trim();

    if (!location) {
        return "غير محدد";
    }

    const isGoogleMapsUrl =
        location.includes("google.com/maps") ||
        location.includes("maps.google.com") ||
        location.includes("maps.app.goo.gl");

    if (isGoogleMapsUrl) {
        return location;
    }

    const coordinates = parseCoordinates(location);

    if (coordinates) {
        return `https://www.google.com/maps?q=${encodeURIComponent(coordinates)}`;
    }

    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;
};

/**
 * Builds a concise vendor-facing Arabic order summary matching WhatsApp structure.
 *
 * @param {object | null | undefined} order - Order object from API.
 * @returns {string} Readable Arabic text for vendors.
 */
export const buildOrderClipboardText = (order) => {
    if (!order || typeof order !== "object") {
        return "لا توجد بيانات للطلب";
    }

    const subtotal = formatMoney(order?.subtotal);
    const shipping = formatMoney(order?.shipping);
    const total = formatMoney(order?.total);
    const orderId = order?.id || "غير محدد";
    const customerName = order?.name || "غير محدد";
    const customerPhone = order?.phone || "غير محدد";
    const customerSecondPhone =
        order?.lat &&
        `${order.lat}`.trim() &&
        `${order.lat}`.trim() !== `${order?.phone || ""}`.trim()
            ? order.lat
            : null;
    const localizedStatus = resolveLocalizedOrderStatus(order?.status);
    const address = order?.address || "غير محدد";
    const location = resolveGoogleMapsLocation(order?.location);
    const vendorName = resolveVendorName(order);
    const notes =
        typeof order?.notes === "string" && order.notes.trim() ? order.notes.trim() : "لا يوجد";
    const productsText = buildOrderProductsText(order);

    return `بيانات الطلب:
رقم الطلب: ${orderId}
حالة الطلب: ${localizedStatus}
اسم المطعم: ${vendorName}

بيانات الزبون:
اسم الزبون: ${customerName}
رقم الزبون: ${customerPhone}
${customerSecondPhone ? `رقم الزبون 2: ${customerSecondPhone}\n` : ""}العنوان: ${address}
الموقع (Google Maps): ${location}

الحساب:
قيمة الطلب: ${subtotal}
الشحن: ${shipping}
المجموع: ${total}

المنتجات:
${productsText}

ملاحظات الطلب:
${notes}`;
};

/**
 * Copies arbitrary text to clipboard with a fallback for older browsers.
 *
 * @param {string} text - Text to copy.
 * @returns {Promise<void>}
 */
export const copyTextToClipboard = async (text) => {
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }

    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.setAttribute("readonly", "");
    textArea.style.position = "absolute";
    textArea.style.left = "-9999px";
    document.body.appendChild(textArea);
    textArea.select();

    const isCopied = document.execCommand("copy");
    document.body.removeChild(textArea);

    if (!isCopied) {
        throw new Error("Failed to copy text");
    }
};
